import React, { Fragment, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

// Components

import IncomeItem from '@components/ssap/pages/pageIncludes/summary/IncomeItem';
import SubHeader from '@components/ssap/headers/SubHeader';

// Context

import { Context } from '@contexts/ssapSummaryContext';

// Hooks

import { useSummaryItemDiffRegister } from '@hooks/useSummaryItemDiffRegister';

// Utils

import { currencyTools } from '@utils/tools/currencyTools';
import { transformDropdownOptionsToObject } from '@utils/normalize/transformDropdownOptionsToObject';
import { isEqual, pick } from '@utils/miscellaneous/objectHelpers';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';


const DeductionsSummarySection = props => {

    const DEDUCTION_SOURCES_LIST = transformDropdownOptionsToObject(DROPDOWN_OPTIONS.DEDUCTION_SOURCES);

    const {
        applicantGuid,
        expenses,
        isFinalSummaryPage,
        isSsapInReviewMode,
        lists: {
            FREQUENCIES_LIST
        },
        previousData,
        processDate
    } = props;

    const summaryContext = useContext(Context);

    const {
        summaryItemsComparisonEnabled
    } = summaryContext;

    const previousExpenses = useMemo(
        () => {
            const index = previousData.taxHousehold.householdMember.findIndex(hhm => hhm.applicantGuid === applicantGuid);
            if(~index) {
                const {
                    expenses
                } = previousData.taxHousehold.householdMember[index];
                return expenses;
            }
            return [];
        },
        [ previousData ]
    );

    const [
        arePreviousDeductionsShown,
        toggleDeductionsVisibility
    ] = useState(!previousExpenses.length);

    const handleToggleLinkClick = e => {
        e.preventDefault();
        toggleDeductionsVisibility(isShown => !isShown);
    };

    const getDeductions = (deductions = [], arePrevious = false) => {
        if(deductions.length === 0 && arePrevious) {
            return (
                <div className = 'grid-row' >
                    <FormattedMessage id = 'common.no_text' />
                </div>
            );
        }

        return deductions.map((expense, idx) => {
            const {
                amount,
                type,
                frequency,
                incomeStartDate,
                incomeEndDate
            } = expense;

            return (
                <IncomeItem
                    key = { `${ type }_${ idx }` }
                    id={`Deductions-${applicantGuid}-${type}`}
                    type = { DEDUCTION_SOURCES_LIST[type] }
                    amount = { currencyTools.getMoneyFormat(amount, true) }
                    frequency = { FREQUENCIES_LIST[frequency] }
                    incomeStartDate = { processDate(incomeStartDate) }
                    incomeEndDate = { processDate(incomeEndDate) }
                    isStrikeThroughValue = { arePrevious }
                />
            );
        });
    };

    const toggleLink = (
        <a
            href = '#'
            onClick = { handleToggleLinkClick }
            className = 'instruction_link instruction_link_p'
        >
            <FormattedMessage id = 'ssap.summary.show_previous_values_link' />
        </a>
    );

    const filterDeductionProps = (expenses = []) => {
        return expenses.map(income => {
            return pick(
                income,
                [
                    'type',
                    'sourceName',
                    'amount',
                    'frequency',
                    'incomeStartDate',
                    'incomeEndDate'
                ]
            );
        });
    };

    const diffs = useMemo(
        () => {
            return {
                status: !isEqual(filterDeductionProps(expenses), filterDeductionProps(previousExpenses))
            };
        },
        [ expenses, previousExpenses ]
    );

    const linkProps =  (
        summaryItemsComparisonEnabled
        && diffs.status
        && previousExpenses.length
    )
        ? {
            auxText: 'common.non_internationalized_text',
            auxTextReplacements: { text: toggleLink }
        }
        : { };

    useSummaryItemDiffRegister({
        diffs: { status: expenses.length === 0 ? false : diffs.status },
        summaryContext,
        values: {
            category: 'expenses',
            value: expenses,
            previousValue: previousExpenses,
            textCode: applicantGuid
        }
    });

    if(expenses.length === 0) {
        return null;
    }

    // We highlight background only on Final Review page (SSAP in edit mode) if we are tracking a diff
    const displayUpdatedRow = (diffs.status && isFinalSummaryPage && !isSsapInReviewMode)
        ? 'bg-updated_fields'
        : '';

    return (
        <Fragment>
            <SubHeader
                { ...{
                    headerClassName: 'gi-heading-summary summary__header--no-border',
                    text: 'ssap.subheader.income_deductions',
                    dataTestId: `Income-Deductions-Header-${applicantGuid}`,
                    ...linkProps
                } }
            />
            <div className="grid-row gi-table__responsive">
                <table className="usa-table usa-table--borderless gi-table gi-table__borderless income-table">
                    <thead>
                        <IncomeItem
                            id={`Deductions-${applicantGuid}-Header`}
                            amount = 'common.how_much_without_question_mark'
                            design = 'header'
                            frequency = 'common.how_often_without_question_mark'
                            incomeStartDate = 'common.start_date'
                            incomeEndDate = 'common.end_date'
                            type = 'common.deduction_source'
                        />
                    </thead>
                    <tbody>
                        <div className = { displayUpdatedRow } >
                            {
                                arePreviousDeductionsShown && getDeductions(previousExpenses, true)
                            }
                            {
                                getDeductions(expenses)
                            }
                        </div>
                    </tbody>
                </table>
            </div>
        </Fragment> 
    );
};

export default DeductionsSummarySection;